import { FC } from 'react';
import AnalyticsExportPageFrame, {
  type AnalyticsExportPageProfile,
} from './AnalyticsExportPageFrame';
import { useAnalyticsExportTheme } from './analyticsExportTheme';

export interface AnalyticsExportPostCard {
  key: string;
  imageUrl: string | null;
  formatLabel: string;
  headlineValue: string;
  caption: string;
  metricsLine: string;
}

interface Props {
  title: string;
  subtitle: string;
  posts: AnalyticsExportPostCard[];
  profile: AnalyticsExportPageProfile;
  pageNumber: number;
  totalPages: number;
}

const AnalyticsExportPostsPage: FC<Props> = ({
  title,
  subtitle,
  posts,
  profile,
  pageNumber,
  totalPages,
}) => {
  const theme = useAnalyticsExportTheme();

  return (
    <AnalyticsExportPageFrame
      title={title}
      subtitle={subtitle}
      profile={profile}
      pageNumber={pageNumber}
      totalPages={totalPages}
      dataCy="analytics_export-public_posts-page"
    >
      <div className="tw-flex tw-flex-1 tw-items-start tw-gap-6 tw-py-8">
        {posts.map((post) => (
          <div
            key={post.key}
            className="analytics-export-post-card tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-3"
          >
            {/* Chrome drops `aspect-ratio` when printing, so the square comes
                from percentage padding and the preview is positioned over it. */}
            <div
              style={{ paddingTop: '100%' }}
              className={`tw-relative tw-w-full tw-overflow-hidden tw-rounded-2xl ${theme.mediaPlaceholder}`}
            >
              {post.imageUrl ? (
                <img
                  src={post.imageUrl}
                  alt=""
                  className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover"
                />
              ) : null}
              <div className="tw-absolute tw-bottom-3 tw-left-3 tw-flex tw-items-center tw-gap-2">
                <span className="tw-rounded-full tw-bg-white tw-px-3 tw-py-1 tw-text-base tw-font-semibold tw-text-grayscale-190">
                  {post.headlineValue}
                </span>
                <span className="tw-rounded-full tw-bg-white tw-px-3 tw-py-1 tw-text-base tw-font-semibold tw-text-grayscale-190">
                  {post.formatLabel}
                </span>
              </div>
            </div>
            <div
              className={`${theme.tile} tw-flex tw-flex-col tw-gap-2 tw-rounded-2xl tw-px-5 tw-py-4`}
            >
              {/* Two lines whatever the caption is, so every card in the row
                  ends at the same height. */}
              <p
                className={`tw-line-clamp-2 tw-h-12 tw-text-base ${theme.tileValue}`}
              >
                {post.caption}
              </p>
              <p
                className={`${theme.accent} tw-truncate tw-text-sm tw-font-medium`}
              >
                {post.metricsLine}
              </p>
            </div>
          </div>
        ))}
      </div>
    </AnalyticsExportPageFrame>
  );
};

export default AnalyticsExportPostsPage;
